import { useEffect, useMemo, useState } from 'react';
import { apiUrl } from '../../shared/api/apiConfig';
import { formatDisplayDateTime } from '../../shared/utils/dateFormat';
import './RegisterBindingsPage.css';

const EMPTY_FORM = {
  registerId: '',
  scannerDeviceId: '',
  scaleDeviceId: '',
};

function sortBindings(bindings) {
  return [...bindings].sort((left, right) => left.registerId.localeCompare(right.registerId));
}

function sortDevices(devices) {
  return [...devices].sort((left, right) => left.deviceId.localeCompare(right.deviceId));
}

function sortRequests(items) {
  return [...items].sort((left, right) => {
    const timeDelta = new Date(right.issuedAtUtc) - new Date(left.issuedAtUtc);
    if (timeDelta !== 0) {
      return timeDelta;
    }

    return String(right.requestId ?? '').localeCompare(String(left.requestId ?? ''));
  });
}

function isActiveRequest(request) {
  return request?.requestStatus !== 'Completed' && request?.requestStatus !== 'Cancelled';
}

function sortRequestLoads(items) {
  return [...items].sort((left, right) => {
    const timeDelta = new Date(right.createdAtUtc) - new Date(left.createdAtUtc);
    if (timeDelta !== 0) {
      return timeDelta;
    }

    return String(right.loadId ?? '').localeCompare(String(left.loadId ?? ''));
  });
}

function formatDate(value) {
  return formatDisplayDateTime(value, 'Unknown');
}

function deviceLabel(device) {
  return `${device.deviceId} - ${device.name} - ${device.deviceStatus}`;
}

function isActiveDevice(device) {
  return String(device.deviceStatus).toLowerCase() === 'active';
}

function statusClass(value) {
  return String(value ?? 'unknown').toLowerCase().replace(/\s+/g, '-');
}

function requestHeadingLabel(request) {
  return `Register ${request.registerId} - ${request.requestDirection}${request.requestLoadId ? ` - load ${request.requestLoadId}` : ''}`;
}

function loadHeadingLabel(requestLoad) {
  if (!requestLoad) {
    return 'No active load';
  }

  return `${requestLoad.loadId} - ${requestLoad.name}`;
}

function deriveRequestLoadStatus(requestLoad, relatedRequests) {
  if (requestLoad?.status) {
    return requestLoad.status;
  }

  if (Array.isArray(relatedRequests) && relatedRequests.length > 0) {
    if (relatedRequests.some((request) => (
      request.requestStatus !== 'Completed' && request.requestStatus !== 'Cancelled'
    ))) {
      return 'InProgress';
    }

    return 'Completed';
  }

  return 'Open';
}

function formatRequestLoadStatus(status) {
  if (!status) {
    return 'Unknown';
  }

  return status === 'InProgress' ? 'In progress' : status;
}

function getRegisterRelatedRequests(requestLoad, registerId) {
  if (!requestLoad || !registerId) {
    return [];
  }

  return sortRequests(
    (requestLoad.weighingRequests ?? []).filter((request) => request.registerId === registerId),
  );
}

function latestCardTime(card) {
  const timestamps = [
    card.outboundLoad?.createdAtUtc ? new Date(card.outboundLoad.createdAtUtc).getTime() : 0,
    ...card.inboundLoads.map((requestLoad) => (
      requestLoad?.createdAtUtc ? new Date(requestLoad.createdAtUtc).getTime() : 0
    )),
  ];

  return Math.max(...timestamps);
}

export default function RegisterBindingsPage() {
  const [devices, setDevices] = useState([]);
  const [bindings, setBindings] = useState([]);
  const [requests, setRequests] = useState([]);
  const [requestLoads, setRequestLoads] = useState([]);
  const [selectedRegisterId, setSelectedRegisterId] = useState('');
  const [bindingFilter, setBindingFilter] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [loadState, setLoadState] = useState({ type: 'idle', message: '' });
  const [saveState, setSaveState] = useState({ type: 'idle', message: '' });
  const [toggleState, setToggleState] = useState({ registerId: '', type: 'idle', message: '' });

  useEffect(() => {
    let active = true;

    async function loadPageData() {
      setLoadState({ type: 'pending', message: 'Loading devices, bindings, requests, and request loads...' });

      try {
        const [devicesResponse, bindingsResponse, requestsResponse, requestLoadsResponse] = await Promise.all([
          fetch(apiUrl('/devices')),
          fetch(apiUrl('/registerbindings')),
          fetch(apiUrl('/weighingrequests?take=250')),
          fetch(apiUrl('/requestloads?take=250')),
        ]);

        if (!devicesResponse.ok || !bindingsResponse.ok || !requestsResponse.ok || !requestLoadsResponse.ok) {
          throw new Error('Failed to load register binding data.');
        }

        const [devicesPayload, bindingsPayload, requestsPayload, requestLoadsPayload] = await Promise.all([
          devicesResponse.json(),
          bindingsResponse.json(),
          requestsResponse.json(),
          requestLoadsResponse.json(),
        ]);

        if (!active) {
          return;
        }

        setDevices(sortDevices(devicesPayload ?? []));
        setBindings(sortBindings(bindingsPayload ?? []));
        setRequests(sortRequests(requestsPayload ?? []));
        setRequestLoads(sortRequestLoads(requestLoadsPayload ?? []));
        setLoadState({ type: 'success', message: '' });
      } catch (error) {
        if (!active) {
          return;
        }

        setLoadState({
          type: 'error',
          message: error instanceof Error ? error.message : 'Failed to load register binding data.',
        });
      }
    }

    loadPageData();

    return () => {
      active = false;
    };
  }, []);

  const activeBoundDeviceIds = useMemo(() => {
    const ids = new Set();

    bindings
      .filter((binding) => String(binding.status).toLowerCase() === 'active')
      .forEach((binding) => {
        ids.add(binding.scannerDeviceId);
        ids.add(binding.scaleDeviceId);
      });

    return ids;
  }, [bindings]);

  const availableScanners = useMemo(
    () =>
      devices.filter(
        (device) =>
          device.deviceType === 'Scanner'
          && isActiveDevice(device)
          && !activeBoundDeviceIds.has(device.deviceId),
      ),
    [devices, activeBoundDeviceIds],
  );

  const availableScales = useMemo(
    () =>
      devices.filter(
        (device) =>
          device.deviceType === 'Scale'
          && isActiveDevice(device)
          && !activeBoundDeviceIds.has(device.deviceId),
      ),
    [devices, activeBoundDeviceIds],
  );

  const filteredBindings = useMemo(() => {
    const normalizedFilter = bindingFilter.trim().toLowerCase();

    if (!normalizedFilter) {
      return bindings;
    }

    return bindings.filter((binding) =>
      [
        binding.registerId,
        binding.scannerDeviceId,
        binding.scaleDeviceId,
        binding.status,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedFilter)),
    );
  }, [bindingFilter, bindings]);

  const selectedBinding = useMemo(
    () => filteredBindings.find((binding) => binding.registerId === selectedRegisterId) ?? null,
    [filteredBindings, selectedRegisterId],
  );

  const requestCountByRegisterId = useMemo(() => {
    const counts = new Map();

    requests.forEach((request) => {
      const currentCount = counts.get(request.registerId) ?? 0;
      counts.set(request.registerId, currentCount + 1);
    });

    return counts;
  }, [requests]);

  const requestLoadCountByRegisterId = useMemo(() => {
    const counts = new Map();

    requestLoads.forEach((requestLoad) => {
      const currentCount = counts.get(requestLoad.registerId) ?? 0;
      counts.set(requestLoad.registerId, currentCount + 1);
    });

    return counts;
  }, [requestLoads]);

  const requestLoadMap = useMemo(
    () => new Map(requestLoads.map((requestLoad) => [requestLoad.loadId, requestLoad])),
    [requestLoads],
  );

  const activeRequestByRegisterId = useMemo(() => {
    const activeRequests = new Map();

    sortRequests(requests)
      .filter(isActiveRequest)
      .forEach((request) => {
        if (!activeRequests.has(request.registerId)) {
          activeRequests.set(request.registerId, request);
        }
      });

    return activeRequests;
  }, [requests]);

  const activeLoadByRegisterId = useMemo(() => {
    const activeLoads = new Map();

    activeRequestByRegisterId.forEach((request, registerId) => {
      const matchedLoad = requestLoadMap.get(request.requestLoadId);
      if (matchedLoad?.registerId === registerId) {
        activeLoads.set(registerId, matchedLoad);
      }
    });

    return activeLoads;
  }, [activeRequestByRegisterId, requestLoadMap]);

  const selectedRelatedRequestLoads = useMemo(() => {
    if (!selectedBinding) {
      return [];
    }

    return sortRequestLoads(
      requestLoads.filter((requestLoad) => requestLoad.registerId === selectedBinding.registerId),
    );
  }, [requestLoads, selectedBinding]);

  const requestLoadCards = useMemo(() => {
    const cardsByKey = new Map();

      selectedRelatedRequestLoads.forEach((requestLoad) => {
        if (requestLoad.direction === 'Outbound') {
          const inboundLoads = requestLoads.filter(
            (candidate) =>
              candidate.direction === 'Inbound'
              && candidate.linkedOutboundLoadId === requestLoad.loadId
              && candidate.registerId === selectedBinding?.registerId,
          );

        cardsByKey.set(`outbound:${requestLoad.loadId}`, {
          key: `outbound:${requestLoad.loadId}`,
          outboundLoad: requestLoad,
          inboundLoads: sortRequestLoads(inboundLoads),
        });
        return;
      }

      const outboundLoad = requestLoad.linkedOutboundLoadId
        ? requestLoadMap.get(requestLoad.linkedOutboundLoadId) ?? null
        : null;
      const key = outboundLoad ? `outbound:${outboundLoad.loadId}` : `inbound:${requestLoad.loadId}`;
      const existingCard = cardsByKey.get(key);
      const nextInboundLoads = sortRequestLoads([
        ...(existingCard?.inboundLoads ?? []),
        requestLoad,
      ].filter((candidate, index, items) => (
        items.findIndex((item) => item.loadId === candidate.loadId) === index
      )));

      cardsByKey.set(key, {
        key,
        outboundLoad: existingCard?.outboundLoad ?? outboundLoad,
        inboundLoads: nextInboundLoads,
      });
    });

    return [...cardsByKey.values()].sort((left, right) => latestCardTime(right) - latestCardTime(left));
  }, [requestLoadMap, requestLoads, selectedBinding?.registerId, selectedRelatedRequestLoads]);

  const activeLoadCount = activeLoadByRegisterId.size;

  useEffect(() => {
    if (filteredBindings.length === 0) {
      if (selectedRegisterId) {
        setSelectedRegisterId('');
      }
      return;
    }

    if (!filteredBindings.some((binding) => binding.registerId === selectedRegisterId)) {
      setSelectedRegisterId(filteredBindings[0].registerId);
    }
  }, [filteredBindings, selectedRegisterId]);

  function handleFieldChange(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  function handleBindingSelect(registerId) {
    setSelectedRegisterId(registerId);
  }

  function resetForm(clearFeedback = true) {
    setForm(EMPTY_FORM);
    if (clearFeedback) {
      setSaveState({ type: 'idle', message: '' });
    }
  }

  async function handleToggleBindingStatus(binding) {
    const nextStatus = binding.status === 'Active' ? 'Retired' : 'Active';
    const actionLabel = nextStatus === 'Active' ? 'Enabling' : 'Disabling';
    setToggleState({
      registerId: binding.registerId,
      type: 'pending',
      message: `${actionLabel} register '${binding.registerId}'...`,
    });

    try {
      const response = await fetch(
        apiUrl(`/registerbindings/${encodeURIComponent(binding.registerId)}/status`),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: nextStatus }),
        },
      );

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.message ?? 'Unable to update register status.');
      }

      const updated = await response.json();
      setBindings((current) =>
        sortBindings(current.map((item) => (item.registerId === updated.registerId ? updated : item))),
      );
      setToggleState({
        registerId: binding.registerId,
        type: 'success',
        message:
          nextStatus === 'Active'
            ? `Register '${binding.registerId}' is enabled and its devices are reserved again.`
            : `Register '${binding.registerId}' is disabled and its devices are now available for new registers.`,
      });
    } catch (error) {
      setToggleState({
        registerId: binding.registerId,
        type: 'error',
        message: error instanceof Error ? error.message : 'Unable to update register status.',
      });
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const payload = {
      registerId: form.registerId.trim(),
      scannerDeviceId: form.scannerDeviceId,
      scaleDeviceId: form.scaleDeviceId,
    };

    if (!payload.registerId || !payload.scannerDeviceId || !payload.scaleDeviceId) {
      setSaveState({ type: 'error', message: 'Register ID, scanner, and scale are required.' });
      return;
    }

    setSaveState({ type: 'pending', message: 'Creating immutable register binding...' });

    try {
      const response = await fetch(apiUrl('/registerbindings'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.message ?? 'Unable to create register binding.');
      }

      const created = await response.json();
      setBindings((current) => sortBindings([...current, created]));
      setSelectedRegisterId(created.registerId);
      setSaveState({
        type: 'success',
        message: `Register '${created.registerId}' created. If the setup changes later, create a new register instead of editing this one.`,
      });
      resetForm(false);
    } catch (error) {
      setSaveState({
        type: 'error',
        message: error instanceof Error ? error.message : 'Unable to create register binding.',
      });
    }
  }

  const isPageLoading = loadState.type === 'pending';
  const pageLoadingMessage = loadState.message || 'Loading devices, bindings, requests, and request loads...';

  return (
    <section className="register-page">
      <div className="register-hero">
        <div>
          <p className="register-eyebrow">Register Bindings</p>
          <h1>Registers and devices</h1>
          <p className="register-subtitle">
            Create immutable register bindings, reserve scanner and scale devices, and inspect the request loads
            associated with the selected register.
          </p>
        </div>
          <div className="register-summary">
            <div>
              <strong>{bindings.length}</strong>
              <span>Total registers</span>
            </div>
            <div>
              <strong>{activeLoadCount}</strong>
              <span>Active loads</span>
            </div>
            <div>
              <strong>{availableScanners.length}</strong>
              <span>Available scanners</span>
            </div>
            <div>
            <strong>{availableScales.length}</strong>
            <span>Available scales</span>
          </div>
        </div>
      </div>

      {isPageLoading ? (
        <div className="register-loading-block" role="status" aria-live="polite">
          <div className="register-loading-spinner" aria-hidden="true" />
          <p>{pageLoadingMessage}</p>
        </div>
      ) : (
        <div className="register-grid">
          <form className="register-card register-form-card" onSubmit={handleSubmit}>
            <div className="register-card-header">
              <div>
                <h2>Create register binding</h2>
              </div>
            </div>

            <div className="register-rule">
              Bindings are immutable. If the scanner or scale setup changes later, create a new register binding
              instead of editing the existing one.
            </div>

            <label className="register-field">
              <span>Register ID</span>
              <input
                name="registerId"
                value={form.registerId}
                onChange={handleFieldChange}
                placeholder="e.g. REG-001"
              />
            </label>

            <label className="register-field">
              <span>Scanner</span>
              <select name="scannerDeviceId" value={form.scannerDeviceId} onChange={handleFieldChange}>
                <option value="">Select scanner</option>
                {availableScanners.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {deviceLabel(device)}
                  </option>
                ))}
              </select>
            </label>

            <label className="register-field">
              <span>Scale</span>
              <select name="scaleDeviceId" value={form.scaleDeviceId} onChange={handleFieldChange}>
                <option value="">Select scale</option>
                {availableScales.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {deviceLabel(device)}
                  </option>
                ))}
              </select>
            </label>

            <div className="register-actions">
              <button type="submit" className="register-primary-btn">
                Create register
              </button>
              <button type="button" className="register-secondary-btn" onClick={() => resetForm()}>
                Reset
              </button>
            </div>

            {saveState.message ? (
              <p className={`register-feedback register-feedback-${saveState.type}`}>{saveState.message}</p>
            ) : null}
          </form>

          <div className="register-stack">
            <div className="register-card register-list-card">
              <div className="register-card-header">
                <div>
                  <h2>Registered bindings</h2>
                  <p>Filter and select a register binding to view its related request loads.</p>
                </div>
              </div>

              <label className="register-filter-field">
                <span>Filter bindings</span>
                <input
                  value={bindingFilter}
                  onChange={(event) => setBindingFilter(event.target.value)}
                  placeholder="Search by register, scanner, scale, or status"
                />
              </label>

              {loadState.type === 'error' ? (
                <div className="register-empty">
                  <strong>Unable to load register bindings</strong>
                  <p>{loadState.message}</p>
                </div>
              ) : bindings.length === 0 ? (
                <div className="register-empty">
                  <strong>No register bindings yet</strong>
                  <p>Create the first register binding using the form on the left.</p>
                </div>
              ) : filteredBindings.length === 0 ? (
                <div className="register-empty">
                  <strong>No bindings match this filter</strong>
                  <p>Try a register ID, device ID, or status keyword.</p>
                </div>
              ) : (
                <>
                  <p className="register-filter-summary">
                    Showing {filteredBindings.length} of {bindings.length} bindings
                  </p>

                  <div className="register-list">
                      {filteredBindings.map((binding) => {
                        const isSelected = binding.registerId === selectedBinding?.registerId;
                        const requestCount = requestCountByRegisterId.get(binding.registerId) ?? 0;
                        const requestLoadCount = requestLoadCountByRegisterId.get(binding.registerId) ?? 0;
                        const activeRequest = activeRequestByRegisterId.get(binding.registerId) ?? null;
                        const activeLoad = activeLoadByRegisterId.get(binding.registerId) ?? null;

                        return (
                        <article
                          key={binding.registerId}
                          className={`register-item${isSelected ? ' register-item-selected' : ''}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleBindingSelect(binding.registerId)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleBindingSelect(binding.registerId);
                            }
                          }}
                          aria-pressed={isSelected}
                        >
                          <div className="register-item-top">
                            <div>
                              <h3>{binding.registerId}</h3>
                              <p>Created {formatDate(binding.createdAtUtc)}</p>
                            </div>
                            <div className="register-item-actions">
                              <span className={`register-status register-status-${String(binding.status).toLowerCase()}`}>
                                {binding.status}
                              </span>
                              <button
                                type="button"
                                className="register-toggle-btn"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleToggleBindingStatus(binding);
                                }}
                                disabled={toggleState.type === 'pending' && toggleState.registerId === binding.registerId}
                              >
                                {binding.status === 'Active' ? 'Disable' : 'Enable'}
                              </button>
                            </div>
                          </div>

                          <dl className="register-meta-grid">
                            <div>
                              <dt>Scanner</dt>
                              <dd>{binding.scannerDeviceId}</dd>
                            </div>
                            <div>
                              <dt>Scale</dt>
                              <dd>{binding.scaleDeviceId}</dd>
                            </div>
                              <div>
                                <dt>Requests</dt>
                                <dd>{requestCount}</dd>
                              </div>
                              <div>
                                <dt>Loads</dt>
                                <dd>{requestLoadCount}</dd>
                              </div>
                              <div>
                                <dt>Active load</dt>
                                <dd>{activeLoad ? activeLoad.loadId : 'None'}</dd>
                              </div>
                              <div>
                                <dt>Active request</dt>
                                <dd>{activeRequest ? activeRequest.requestId : 'None'}</dd>
                              </div>
                            </dl>

                          {toggleState.registerId === binding.registerId && toggleState.message ? (
                            <p className={`register-feedback register-feedback-${toggleState.type}`}>
                              {toggleState.message}
                            </p>
                          ) : null}
                        </article>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            <div className="register-card register-requests-card">
                <div className="register-card-header">
                  <div>
                    <h2>Request loads</h2>
                    <p>
                      {selectedBinding
                        ? `Outbound and linked inbound loads for register '${selectedBinding.registerId}'.`
                        : 'Select a register binding to inspect related request loads.'}
                    </p>
                  </div>
                </div>

              {!selectedBinding ? (
                <div className="register-empty">
                  <strong>No register selected</strong>
                  <p>Select a binding from the list to view its request loads.</p>
                </div>
              ) : requestLoadCards.length === 0 ? (
                <div className="register-empty">
                  <strong>No related request loads yet</strong>
                  <p>This register binding does not have any request loads assigned to it yet.</p>
                </div>
              ) : (
                <div className="register-request-load-list">
                  <section className="register-focus-card">
                    <div>
                      <p className="register-load-direction">Active lane</p>
                      <h3>{selectedBinding.registerId}</h3>
                      <p>
                        {loadHeadingLabel(activeLoadByRegisterId.get(selectedBinding.registerId) ?? null)}
                      </p>
                    </div>
                    <div className="register-focus-meta">
                      <div>
                        <dt>Active request</dt>
                        <dd>{activeRequestByRegisterId.get(selectedBinding.registerId)?.requestId ?? 'None'}</dd>
                      </div>
                      <div>
                        <dt>Total loads</dt>
                        <dd>{requestLoadCountByRegisterId.get(selectedBinding.registerId) ?? 0}</dd>
                      </div>
                    </div>
                  </section>
                  {requestLoadCards.map((card) => {
                    const outboundRequests = getRegisterRelatedRequests(card.outboundLoad, selectedBinding.registerId);
                    const outboundStatus = deriveRequestLoadStatus(card.outboundLoad, outboundRequests);
                    const inboundLoads = card.inboundLoads.length > 0 ? card.inboundLoads : [null];

                    return (
                      <article key={card.key} className="register-request-load-card">
                        <div className="register-request-load-sides">
                          <section className="register-load-panel register-load-panel-outbound">
                            {card.outboundLoad ? (
                              <>
                                <div className="register-load-panel-top">
                                  <div>
                                    <p className="register-load-direction">Outbound</p>
                                    <h3>{card.outboundLoad.name}</h3>
                                    <p>{card.outboundLoad.loadId}</p>
                                  </div>
                                  <span className={`register-request-status register-request-status-${statusClass(outboundStatus)}`}>
                                    {formatRequestLoadStatus(outboundStatus)}
                                  </span>
                                </div>

                                <dl className="register-request-meta-grid">
                                  <div>
                                    <dt>Created</dt>
                                    <dd>{formatDate(card.outboundLoad.createdAtUtc)}</dd>
                                  </div>
                                  <div>
                                    <dt>Register</dt>
                                    <dd>{card.outboundLoad.registerId}</dd>
                                  </div>
                                  <div>
                                    <dt>Register requests</dt>
                                    <dd>{outboundRequests.length}</dd>
                                  </div>
                                  <div>
                                    <dt>Latest request</dt>
                                    <dd>{outboundRequests[0] ? requestHeadingLabel(outboundRequests[0]) : 'No requests on this register'}</dd>
                                  </div>
                                  <div>
                                    <dt>Notes</dt>
                                    <dd>{card.outboundLoad.notes || 'No notes'}</dd>
                                  </div>
                                </dl>
                              </>
                            ) : (
                              <div className="register-load-panel-empty">
                                <strong>No outbound load</strong>
                                <p>This inbound load does not have a matching outbound load in the current dataset.</p>
                              </div>
                            )}
                          </section>

                          <section className="register-load-panel register-load-panel-inbound">
                            
                              {inboundLoads.map((requestLoad) => {
                                if (!requestLoad) {
                                  return (
                                    <div key={`${card.key}:empty`} className="register-load-panel-empty">
                                      <strong>No inbound load yet</strong>
                                      <p>This outbound load does not have a linked inbound request load.</p>
                                    </div>
                                  );
                                }

                                const inboundRequests = getRegisterRelatedRequests(requestLoad, selectedBinding.registerId);
                                const inboundStatus = deriveRequestLoadStatus(requestLoad, inboundRequests);

                                return (
                                  <article key={requestLoad.loadId} className="register-load-panel-nested">
                                    <div className="register-load-panel-top">
                                      <div>
                                        <p className="register-load-direction">Inbound</p>
                                        <h3>{requestLoad.name}</h3>
                                        <p>{requestLoad.loadId}</p>
                                      </div>
                                      <span className={`register-request-status register-request-status-${statusClass(inboundStatus)}`}>
                                        {formatRequestLoadStatus(inboundStatus)}
                                      </span>
                                    </div>

                                    <dl className="register-request-meta-grid">
                                      <div>
                                        <dt>Created</dt>
                                        <dd>{formatDate(requestLoad.createdAtUtc)}</dd>
                                      </div>
                                      <div>
                                        <dt>Register</dt>
                                        <dd>{requestLoad.registerId}</dd>
                                      </div>
                                      <div>
                                        <dt>Register requests</dt>
                                        <dd>{inboundRequests.length}</dd>
                                      </div>
                                      <div>
                                        <dt>Linked outbound</dt>
                                        <dd>{requestLoad.linkedOutboundLoadId || 'Not linked'}</dd>
                                      </div>
                                      <div>
                                        <dt>Notes</dt>
                                        <dd>{requestLoad.notes || 'No notes'}</dd>
                                      </div>
                                    </dl>
                                  </article>
                                );
                              })}
                            
                          </section>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
